import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { extractPropertyMetadata } from './firestoreQueries';
import { createNotification } from './notifications';

/**
 * Create a property application with motivation letter
 */
export async function createPropertyApplication({
  propertyId,
  viewingId = null,
  tenantId,
  motivationLetterUrl,
  motivationLetterPath,
  tenantSnapshot
}) {
  if (!propertyId || !tenantId || !motivationLetterUrl) {
    throw new Error('Missing required application fields');
  }

  let propertyMetadata = {
    agencyId: null,
    clientLinkId: null,
    ownerId: null,
    propertyId
  };
  let propertySnapshot = null;

  try {
    const propertyDoc = await getDoc(doc(db, 'koten', propertyId));
    if (propertyDoc.exists()) {
      const propertyData = { id: propertyDoc.id, ...propertyDoc.data() };
      propertyMetadata = extractPropertyMetadata(propertyData);
      propertySnapshot = {
        title: propertyData.titel || propertyData.title || null,
        city: propertyData.stad || propertyData.city || null
      };
    }
  } catch (error) {
    console.warn('Could not fetch property metadata for application:', error);
  }

  const applicationData = {
    propertyId,
    viewingId: viewingId || null,
    tenantId,
    status: 'submitted',
    reviewStatus: 'unread',
    motivationLetterUrl,
    motivationLetterPath: motivationLetterPath || null,
    agencyId: propertyMetadata.agencyId,
    clientLinkId: propertyMetadata.clientLinkId,
    ownerId: propertyMetadata.ownerId,
    tenantSnapshot: tenantSnapshot || null,
    propertySnapshot,
    submittedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const appRef = await addDoc(collection(db, 'propertyApplications'), applicationData);
  return appRef.id;
}

export async function getApplicationsForProperty(propertyId) {
  const applicationsQuery = query(
    collection(db, 'propertyApplications'),
    where('propertyId', '==', propertyId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(applicationsQuery);
  return snapshot.docs.map(docSnap => ({
    id: docSnap.id,
    ...docSnap.data()
  }));
}

export async function getApplicationsForTenant(tenantId) {
  const applicationsQuery = query(
    collection(db, 'propertyApplications'),
    where('tenantId', '==', tenantId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(applicationsQuery);
  return snapshot.docs.map(docSnap => ({
    id: docSnap.id,
    ...docSnap.data()
  }));
}

export async function proposeApplication(applicationId, agencyId) {
  const applicationRef = doc(db, 'propertyApplications', applicationId);
  const applicationDoc = await getDoc(applicationRef);

  if (!applicationDoc.exists()) {
    throw new Error('Application not found');
  }

  await updateDoc(applicationRef, {
    status: 'proposed',
    proposedByAgencyId: agencyId,
    proposedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}

export async function finalizeApplicationSelection(applicationId, propertyId, ownerId) {
  const applications = await getApplicationsForProperty(propertyId);
  const selected = applications.find(app => app.id === applicationId);

  if (!selected) {
    throw new Error('Selected application not found');
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();

  applications.forEach(app => {
    const appRef = doc(db, 'propertyApplications', app.id);
    const newStatus = app.id === applicationId ? 'selected' : 'rejected';
    const updatePayload = {
      status: newStatus,
      updatedAt: now
    };
    if (app.id === applicationId) {
      updatePayload.selectedAt = now;
      updatePayload.selectedByOwnerId = ownerId;
    } else {
      updatePayload.rejectedAt = now;
    }
    batch.update(appRef, updatePayload);
  });

  const propertyRef = doc(db, 'koten', propertyId);
  batch.update(propertyRef, {
    status: 'handed_over',
    'management.finalTenantId': selected.tenantId,
    'management.lastOwnerActionAt': now,
    updatedAt: now
  });

  await batch.commit();

  try {
    await createNotification({
      recipientUserId: ownerId,
      type: 'contract_required',
      title: 'Maak contract',
      message: 'Een kandidaat is gekozen. Maak nu het contract aan.',
      propertyId,
      tenantId: selected.tenantId,
      applicationId
    });
  } catch (error) {
    console.warn('Could not create contract notification:', error);
  }
}
